/* =============================================================================
   TAB-COORDINATOR — Master-Election + Multi-Tab-Realtime-Relay
   =============================================================================
   Problem (S29 Funktionstest): bei mehreren offenen Browser-Tabs öffnet jeder
   Tab einen eigenen Supabase-Realtime-WebSocket. Bei zwei oder mehr Tabs
   beobachten wir Channel-Flapping (`CLOSED → SUBSCRIBED → CLOSED`) und
   unnötigen Connection-Druck — die Supabase Free-Tier-Connection-Quote
   pro Auth-Token wird gerissen, Reconnect-Logik in storage.js geht in
   Endlos-Backoff.

   S30-V1 (Heartbeat-Pattern, FUNKTIONSTEST VERWORFEN): erste Version nutzte
   BroadcastChannel-Heartbeats für die Master-Election. Funktionstest zeigte
   sofort einen kritischen Defekt: Chrome (und andere Browser) drosseln
   setInterval/setTimeout in Hintergrund-Tabs auf min. 1 Minute, sodass
   Master-Heartbeats aus inaktiven Tabs nicht mehr ankommen. Der Watchdog
   im aktiven Tab feuerte fälschlich „Master ausgefallen", und der aktive
   Tab schwang ungewollt auf Master um → ZWEI Master mit jeweils eigenen
   WebSockets → genau das Anti-Pattern, das wir vermeiden wollten.

   S30-V2 (Web Locks API, dieser Stand): Master-Election läuft jetzt über
   `navigator.locks.request` (Web Standard, allen Browsern mit
   BroadcastChannel-Support vorhanden — Chrome 69+, Firefox 96+,
   Safari 15.4+). Web Locks sind **nicht throttle-anfällig**, der Browser
   garantiert genau einen Lock-Holder pro Origin und gibt Locks bei
   Tab-Close automatisch frei (der nächste Tab in der Queue gewinnt sofort).
   Memory-Lesson `feedback_gg_hard_way` greift: kein Watchdog-Heartbeat-
   Hack, sondern der saubere Vollausbau über die Browser-Standard-API
   für genau diesen Use Case.

   Architektur:
   - **Master-Election:** `navigator.locks.request('gg-realtime-master',
     { mode: 'exclusive' })` mit einer langlebigen Promise im Callback.
     Solange die Promise nicht resolved, hält der Tab den Lock und ist
     Master. Bei Tab-Close oder `shutdown()` wird die Promise resolved →
     Lock-Freigabe → nächster wartender Tab gewinnt.
   - **Initial-Probe:** ein zusätzlicher `ifAvailable: true`-Request am
     Init bestimmt SOFORT (ohne Wartezeit), ob der Lock frei oder belegt
     ist — damit `getRole()` direkt nach `init()` einen brauchbaren Wert
     liefert. Race-Fenster zwischen Probe und echter Request ist
     mikroskopisch klein und nur ein „Follower → Master"-Übergang, kein
     Datenverlust.
   - **Payload-Relay:** unverändert via BroadcastChannel. Master broadcastet
     postgres_changes-Payloads, Follower empfängt sie via `onRelay`. Der
     BroadcastChannel-Transport ist nicht von der setInterval-Drosselung
     betroffen (Browser-API-Calls werden auch in Background-Tabs zugestellt,
     nur die JavaScript-Verarbeitung kann verzögert sein — das ist hier
     akzeptabel, weil der Master immer aktiv ist, wenn er sendet).

   Public API (unverändert ggü. V1):
     getRole()               // 'master' | 'follower'
     onRoleChange(cb)        // cb(newRole, oldRole) — returns off()
     relay(topic, payload)   // nur Master — broadcastet an alle Tabs
     onRelay(topic, cb)      // cb(payload) bei eingehendem Relay
                             // returns off()
     init()                  // idempotent; wird auto-aufgerufen
     shutdown()              // für Tests / Cleanup
     getTabId()              // für Logging/Debug

   Browser-Kompat: ohne Web Locks API ODER ohne BroadcastChannel-Support
   läuft der Tab IMMER als Master (Single-Tab-Modus) — legacy-Verhalten,
   kein Funktions-Regress.
============================================================================ */

const LOCK_NAME = 'gg-realtime-master';
const CHANNEL_NAME = 'gg-realtime-relay-v2';

let _bc = null;
let _tabId = null;
let _role = 'follower'; // 'master' | 'follower'
let _initialized = false;
let _lockHoldResolve = null;   // Wenn nicht null, halten wir den Master-Lock

const _roleListeners = new Set();
const _relayListeners = new Map(); // topic -> Set<callback>

function _uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try { return crypto.randomUUID(); } catch (_) {}
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

function _post(type, data) {
  if (!_bc) return;
  try {
    _bc.postMessage(Object.assign({ type, from: _tabId }, data || {}));
  } catch (e) {
    console.warn('[tab-coordinator] postMessage failed:', e);
  }
}

function _setRole(newRole) {
  if (newRole === _role) return;
  const oldRole = _role;
  _role = newRole;
  console.info('[tab-coordinator] role: ' + oldRole + ' → ' + newRole + ' (tab=' + (_tabId || '?').slice(0, 8) + ')');
  for (const cb of Array.from(_roleListeners)) {
    try { cb(newRole, oldRole); }
    catch (e) { console.error('[tab-coordinator] role listener failed:', e); }
  }
}

function _handleMessage(event) {
  const msg = event.data;
  if (!msg || msg.from === _tabId) return;
  if (msg.type !== 'relay') return; // V2: BroadcastChannel transportiert NUR Relay-Payloads
  if (_role !== 'follower') return; // Master ignoriert Relays (er ist die Quelle)
  if (!msg.topic) return;
  const set = _relayListeners.get(msg.topic);
  if (!set || set.size === 0) return;
  for (const cb of Array.from(set)) {
    try { cb(msg.payload); }
    catch (e) { console.error('[tab-coordinator] relay listener (' + msg.topic + '):', e); }
  }
}

function _handleBeforeUnload() {
  // Web Locks: Browser gibt den Lock bei Tab-Close automatisch frei,
  // der nächste Tab in der Queue gewinnt sofort. Kein expliziter
  // `master-leaving`-Broadcast nötig.
  if (_lockHoldResolve) {
    try { _lockHoldResolve(); } catch (_) {}
    _lockHoldResolve = null;
  }
  if (_bc) {
    try { _bc.close(); } catch (_) {}
    _bc = null;
  }
}

/**
 * Master-Lock-Loop: blockiert (in der Promise im Callback) bis wir entweder
 * den Lock erwerben (→ wir werden Master) oder das Modul shutdown'd wird.
 *
 * Web Locks-Semantik:
 *  - request() returnt sofort ein Promise, das resolve't, wenn der Callback
 *    fertig ist (= wir haben den Lock und geben ihn wieder frei).
 *  - Solange das Callback eine ungelöste Promise zurückgibt, wird der Lock
 *    gehalten.
 *  - Mehrere Tabs queue'n sich; der Browser garantiert Single-Holder pro
 *    Origin.
 */
async function _runMasterLockLoop() {
  if (typeof navigator === 'undefined' || !navigator.locks) return;
  try {
    await navigator.locks.request(LOCK_NAME, { mode: 'exclusive' }, async (lock) => {
      if (!lock) return;
      if (!_initialized) {
        // shutdown() lief während wir auf den Lock warteten → sofort wieder freigeben
        return;
      }
      // Wir haben den Lock — wir sind Master.
      _setRole('master');
      // Lock halten, bis shutdown() oder beforeunload den Hold löst.
      await new Promise(resolveHold => {
        _lockHoldResolve = resolveHold;
      });
      _lockHoldResolve = null;
      // Hier ist Callback-Ende → Lock wird automatisch freigegeben → nächster
      // wartender Tab in der Queue gewinnt sofort. Web Locks API bringt das
      // ohne expliziten Broadcast-Mechanismus, weil der Browser die Queue hält.
    });
  } catch (e) {
    console.error('[tab-coordinator] Master-Lock-Loop failed:', e);
  }
}

export async function init() {
  if (_initialized) return;
  _initialized = true;
  _tabId = _uuid();

  // BroadcastChannel für Relay (V2: nur noch Payload-Transport, keine Heartbeats)
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      _bc = new BroadcastChannel(CHANNEL_NAME);
      _bc.addEventListener('message', _handleMessage);
    } catch (e) {
      console.warn('[tab-coordinator] BroadcastChannel-Init failed:', e);
      _bc = null;
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', _handleBeforeUnload);
  }

  // Web Locks Browser-Support prüfen
  if (typeof navigator === 'undefined' || !navigator.locks) {
    console.warn('[tab-coordinator] Web Locks API nicht verfügbar — Tab läuft als Master (Single-Tab-Modus).');
    _setRole('master');
    return;
  }

  // Schritt 1: Probe — ist der Lock gerade frei? Liefert den Initial-Status sofort.
  // ifAvailable:true gibt unmittelbar zurück: probeLock=null wenn belegt,
  // probeLock=<lock> wenn frei. Wir nutzen die Probe NICHT um den Lock zu
  // halten — wir geben ihn sofort wieder frei (durch Callback-Return) und
  // queue'n uns dann im echten Request unten an die nächste Position.
  let initialMasterCandidate = false;
  try {
    await navigator.locks.request(LOCK_NAME, { mode: 'exclusive', ifAvailable: true }, async (probeLock) => {
      if (probeLock) {
        // Lock war frei. Wahrscheinlich werden wir gleich Master.
        initialMasterCandidate = true;
      } else {
        // Lock ist belegt. Wir sind Follower.
        _setRole('follower');
      }
    });
  } catch (e) {
    console.warn('[tab-coordinator] Probe-Lock failed:', e);
  }

  // Schritt 2: Echter Master-Lock-Loop. Bei freiem Lock gewinnt der sofort
  // (initialMasterCandidate=true → _setRole('master') passiert im Loop).
  // Bei belegtem Lock queue'n wir uns und werden später Master, wenn der
  // bisherige Halter geht.
  void initialMasterCandidate; // nur als Doku — der Loop entscheidet
  _runMasterLockLoop();
}

export function shutdown() {
  if (!_initialized) return;
  _initialized = false;
  // Lock freigeben — Master-Lock-Loop returnt durch resolveHold
  if (_lockHoldResolve) {
    try { _lockHoldResolve(); } catch (_) {}
    _lockHoldResolve = null;
  }
  if (_bc) {
    try { _bc.removeEventListener('message', _handleMessage); } catch (_) {}
    try { _bc.close(); } catch (_) {}
    _bc = null;
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('beforeunload', _handleBeforeUnload);
  }
  _roleListeners.clear();
  _relayListeners.clear();
  _role = 'follower';
  _tabId = null;
}

export function getRole() {
  return _role;
}

export function getTabId() {
  return _tabId;
}

export function onRoleChange(cb) {
  if (typeof cb !== 'function') return () => {};
  _roleListeners.add(cb);
  return function off() { _roleListeners.delete(cb); };
}

export function relay(topic, payload) {
  if (_role !== 'master') return; // Sicherheitsnetz — nur Master broadcastet
  if (!topic) return;
  _post('relay', { topic, payload });
}

export function onRelay(topic, cb) {
  if (!topic || typeof cb !== 'function') return () => {};
  if (!_relayListeners.has(topic)) _relayListeners.set(topic, new Set());
  const set = _relayListeners.get(topic);
  set.add(cb);
  return function off() {
    set.delete(cb);
    if (set.size === 0) _relayListeners.delete(topic);
  };
}

// Auto-Init beim Modul-Load — Browser-Code feuert init() ohne Aufrufer.
// Tests setzen window.__GG_TEST_ENV__ = true, damit der Auto-Init übersprungen
// wird und Tests `init()` + `shutdown()` symmetrisch steuern können.
if (typeof window !== 'undefined') {
  if (!window.__GG_TEST_ENV__) {
    // init() ist async, aber wir warten hier nicht — die Initialisierung läuft
    // im Hintergrund. coordinatedSubscribe in storage.js liest tc.role bei
    // jedem Subscribe; falls die Probe noch nicht durchgelaufen ist, ist der
    // Default 'follower' → kein WebSocket → wartet auf Role-Change.
    init().catch(e => console.error('[tab-coordinator] init failed:', e));
  }
}

// Backward-Compat — API auch als window.App.tabCoordinator verfügbar
if (typeof window !== 'undefined') {
  window.App = window.App || {};
  window.App.tabCoordinator = {
    init,
    shutdown,
    getRole,
    getTabId,
    onRoleChange,
    relay,
    onRelay,
    get role() { return getRole(); }
  };
}
